import * as React from 'react';
import { Avatar, Box, IconButton, Link, useMediaQuery } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { tokens, WIDE_LAYOUT_QUERY } from '../DashboardTheme/DashboardTheme';
import logo from '../../img/logo.webp';

const INSTAGRAM = 'https://www.instagram.com/mario__vivenzio/';
const FACEBOOK = 'https://www.facebook.com/mario.vivenzio.58/';

const NAV_LINKS = [
    { label: 'Chi siamo', target: 'chiSiamoSection' },
    { label: 'ONE Championship', target: 'oneChampionshipSection', highlight: true },
    { label: 'Attività', target: 'activitiesSection' },
    { label: 'Sponsors', target: 'sponsorSection' },
    { label: 'Contatti', target: 'contattiSection' },
];

export default function NavBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const isCompact = !useMediaQuery(WIDE_LAYOUT_QUERY);

    const [scrolled, setScrolled] = React.useState(false);
    const [menuOpen, setMenuOpen] = React.useState(false);

    const isHomePage = location.pathname === '/';

    React.useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Lock body scroll while the mobile menu is open.
    React.useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    const goToSection = React.useCallback((id) => {
        setMenuOpen(false);
        if (isHomePage) {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        } else {
            // Land on the home page first, then scroll once it has rendered.
            navigate('/');
            window.setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            }, 120);
        }
    }, [isHomePage, navigate]);

    const handleBrandClick = () => {
        setMenuOpen(false);
        if (isHomePage) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/');
        }
    };

    // The bar sits over the hero only on the home page; elsewhere it is always solid.
    const solid = scrolled || menuOpen || !isHomePage;

    return (
        <>
            <Box
                component="nav"
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1200,
                    backgroundColor: solid ? 'rgba(8, 9, 10, 0.85)' : 'transparent',
                    backdropFilter: solid ? 'blur(14px)' : 'none',
                    borderBottom: `1px solid ${solid ? tokens.line : 'transparent'}`,
                    transition: `all 0.45s ${tokens.ease}`,
                }}
            >
                <Box
                    sx={{
                        maxWidth: 1440,
                        mx: 'auto',
                        px: { xs: 2.5, md: 5 },
                        height: { xs: 68, md: solid ? 76 : 92 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: `height 0.45s ${tokens.ease}`,
                    }}
                >
                    {/* ---------- Brand ---------- */}
                    <Box
                        onClick={handleBrandClick}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                    >
                        <Avatar
                            alt="Logo Vivenzio Gym"
                            src={logo}
                            sx={{
                                width: { xs: 38, md: 46 },
                                height: { xs: 38, md: 46 },
                                border: `1px solid ${tokens.lineStrong}`,
                            }}
                        />
                        <Box>
                            <Box
                                sx={{
                                    fontFamily: tokens.display,
                                    fontSize: { xs: 22, md: 28 },
                                    lineHeight: 1,
                                    letterSpacing: '0.03em',
                                    color: tokens.white,
                                }}
                            >
                                TEAM VIVENZIO
                            </Box>
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'block' },
                                    fontFamily: tokens.heading,
                                    fontSize: 10,
                                    letterSpacing: '0.32em',
                                    textTransform: 'uppercase',
                                    color: tokens.dim,
                                    mt: 0.4,
                                }}
                            >
                                Muay Thai — Saviano
                            </Box>
                        </Box>
                    </Box>

                    {/* ---------- Desktop links ---------- */}
                    {!isCompact && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3.5 }}>
                            {NAV_LINKS.map((link) => (
                                <Box
                                    key={link.label}
                                    onClick={() => goToSection(link.target)}
                                    sx={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        fontFamily: tokens.heading,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        letterSpacing: '0.16em',
                                        textTransform: 'uppercase',
                                        color: link.highlight ? tokens.red : tokens.muted,
                                        py: 0.5,
                                        transition: `color 0.35s ${tokens.ease}`,
                                        '::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            bottom: 0,
                                            width: 0,
                                            height: '2px',
                                            backgroundColor: tokens.red,
                                            transition: `width 0.4s ${tokens.ease}`,
                                        },
                                        ':hover': { color: tokens.white },
                                        ':hover::after': { width: '100%' },
                                    }}
                                >
                                    {link.label}
                                </Box>
                            ))}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                <SocialIcon href={INSTAGRAM} label="Instagram"><InstagramIcon /></SocialIcon>
                                <SocialIcon href={FACEBOOK} label="Facebook"><FacebookIcon /></SocialIcon>
                            </Box>
                        </Box>
                    )}

                    {/* ---------- Mobile toggle ---------- */}
                    {isCompact && (
                        <IconButton
                            onClick={() => setMenuOpen((open) => !open)}
                            aria-label={menuOpen ? 'Chiudi il menu' : 'Apri il menu'}
                            aria-expanded={menuOpen}
                            sx={{ color: tokens.white }}
                        >
                            {menuOpen ? <CloseIcon sx={{ fontSize: 30 }} /> : <MenuIcon sx={{ fontSize: 30 }} />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* ---------- Mobile full-screen menu ---------- */}
            {isCompact && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1100,
                        backgroundColor: 'rgba(8, 9, 10, 0.97)',
                        backdropFilter: 'blur(18px)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        px: 4,
                        opacity: menuOpen ? 1 : 0,
                        visibility: menuOpen ? 'visible' : 'hidden',
                        transition: `opacity 0.4s ${tokens.ease}, visibility 0.4s`,
                    }}
                >
                    {NAV_LINKS.map((link, i) => (
                        <Box
                            key={link.label}
                            onClick={() => goToSection(link.target)}
                            sx={{
                                fontFamily: tokens.display,
                                fontSize: 38,
                                lineHeight: 1.35,
                                // American Captain draws lowercase as caps, but the
                                // fallback used for accents does not — uppercase the
                                // string so "Attività" doesn't render "ATTIVITà".
                                textTransform: 'uppercase',
                                color: link.highlight ? tokens.red : tokens.white,
                                cursor: 'pointer',
                                borderBottom: `1px solid ${tokens.line}`,
                                py: 1.5,
                                opacity: menuOpen ? 1 : 0,
                                transform: menuOpen ? 'none' : 'translateY(16px)',
                                transition: `all 0.5s ${tokens.ease} ${menuOpen ? i * 60 : 0}ms`,
                            }}
                        >
                            {link.label}
                        </Box>
                    ))}

                    <Box sx={{ display: 'flex', gap: 1, mt: 4 }}>
                        <SocialIcon href={INSTAGRAM} label="Instagram"><InstagramIcon /></SocialIcon>
                        <SocialIcon href={FACEBOOK} label="Facebook"><FacebookIcon /></SocialIcon>
                    </Box>
                </Box>
            )}
        </>
    );
}

function SocialIcon({ href, label, children }) {
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            underline="none"
            sx={{
                display: 'grid',
                placeItems: 'center',
                width: 42,
                height: 42,
                color: tokens.muted,
                border: `1px solid ${tokens.line}`,
                transition: `all 0.35s ${tokens.ease}`,
                ':hover': {
                    color: tokens.white,
                    borderColor: tokens.red,
                    backgroundColor: 'rgba(224, 26, 34, 0.12)',
                },
            }}
        >
            {children}
        </Link>
    );
}
